import { Injectable } from '@angular/core';

import { EnableClassTelemetry } from 'app/services/otel.service';

import { QueryParams, Headers, APIService } from './api.service';

export interface BridgeResult<T> {
  result?: T[];
}

export interface BridgeListResult<T> {
  result?: {
    count: number;
    hits: T[];
  };
  request?: {
    endpoint: string;
    params: QueryParams;
    headers: Headers;
  };
}

@EnableClassTelemetry()
@Injectable()
export abstract class BridgeAPIService extends APIService {
  getBaseUrl() {
    const appConfig = (window as any).appConfig || {};
    if (appConfig.blender && appConfig.blender.url) {
      return `${appConfig.blender.url}/backend`;
    }
    return 'http://localhost:8080/backend';
  }

  getBaseHeaders() {
    return {
      Authorization: this.getToken(),
    };
  }

  protected async getPage<T>(
    endpoint: string,
    params: QueryParams,
    headers?: Headers
  ): Promise<BridgeListResult<T>> {
    if (!params.limit) {
      params.limit = 20;
    }

    if (!params.offset) {
      params.offset = 0;
    }

    const result = await this.get<BridgeListResult<T>>(endpoint, params, headers);

    result.request = { endpoint, params, headers };

    return result;
  }

  public async getPrevPage<T>(page: BridgeListResult<T>): Promise<BridgeListResult<T>> {
    const params = Object.assign({}, page.request.params);
    const offset = params.offset as number;
    const limit = params.limit as number;
    params.offset = offset - limit;

    if (offset <= 0) {
      throw new Error('already at first page');
    }

    const result = await this.get<BridgeListResult<T>>(
      page.request.endpoint,
      params,
      page.request.headers
    );

    result.request = {
      endpoint: page.request.endpoint,
      params,
      headers: page.request.headers,
    };

    return result;
  }

  public async getNextPage<T>(page: BridgeListResult<T>): Promise<BridgeListResult<T>> {
    const params = Object.assign({}, page.request.params);
    const offset = params.offset as number;
    const limit = params.limit as number;
    params.offset = offset + limit;

    if (offset + limit > page.result.count) {
      throw new Error('already at last page');
    }

    const result = await this.get<BridgeListResult<T>>(
      page.request.endpoint,
      params,
      page.request.headers
    );

    result.request = {
      endpoint: page.request.endpoint,
      params,
      headers: page.request.headers,
    };

    return result;
  }
}
